import React, { useState, useEffect, useRef } from "react";
import { TranslationHint } from "../../helper/TranslationHint";
import { Modal, Form, Input, InputNumber, Button, Select } from "antd";
import { callApi } from "../../api/apiCaller";
import { GreenNotify, RedNotify } from "../../helper/helper";
import routes from "../../api/routes";
import { mapLinkValidator, MAP_LINK_HINT } from "../../helper/mapLink";
import { Buffer } from "buffer";
import AWS from "aws-sdk";
import { PLACEHOLDER_IMAGE } from "../../helper/placeholderImage";

AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: "us-east-2",
});

const AddCategory = ({ visible, toggleModal, recordData }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [roots, setRoots] = useState([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);
  const { Option } = Select;

  // Title/description are stored as { en, es, ... } translation objects on the backend.
  // A plain <Input> can only display a string, so pick one language to show/edit.
  const displayText = (val) =>
    typeof val === "string"
      ? val
      : val?.en || val?.es || Object.values(val || {})[0] || "";

  useEffect(() => {
    if (visible) {
      if (recordData) {
        form.setFieldsValue({
          title: displayText(recordData?.title),
          description: displayText(recordData?.description),
          // These are stored as GeoJSON objects; an <Input> can only show a string, so
          // show the saved address (an object here rendered as "[object Object]").
          startLocation: recordData?.startLocation?.address,
          endLocation: recordData?.endLocation?.address,
          difficulty: recordData?.difficulty,
          route: recordData?.route,
        });
        setImages(recordData.highlights || [PLACEHOLDER_IMAGE]);
      } else {
        form.resetFields();
        setImages([]);
      }
    }
  }, [visible, recordData, form]);

  useEffect(() => {
    if (visible) {
      callApi(
        "GET",
        `${routes.getAllRoots}`,
        null,
        setIsLoading,
        (res) => {
          if (res.status === 200) {
            setRoots(res.data.routes);
          } else {
            RedNotify(res?.message || "Error Occurred. Please try again later.");
          }
        },
        (error) => {
          RedNotify(
            "An error occurred during the API call. Please try again later.",
            error
          );
        }
      );
    }
  }, [visible]);

  const pickImageFile = () => {
    fileInputRef.current.click();
  };

  const uploadImageOnS3 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const S3 = new AWS.S3();
        const params = {
          Bucket: "goodeway",
          Key: `${10000 + Math.round(Math.random() * 10000)}.png`,
          Body: Buffer.from(
            reader.result.replace(/^data:image\/\w+;base64,/, ""),
            "base64"
          ),
        };
        try {
          let res = await S3.upload(params).promise();
          resolve(res.Location);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const fileList = event.target.files;
    if (fileList.length > 0) {
      try {
        const imgPromises = Array.from(fileList).map(uploadImageOnS3);
        const imgUrls = await Promise.all(imgPromises);
        setImages(imgUrls);
        setFileError("");
      } catch (error) {
        console.error("Error uploading images:", error);
        setFileError("An error occurred while uploading images.");
      }
    } else {
      setFileError("Please select at least one file.");
    }
  };

  const handleSave = () => {
    // Disable the button up front so a slow save cannot be double-submitted; validation
    // failures re-enable it in the .catch below.
    setIsLoading(true);
    form
      .validateFields()
      .then((values) => {
        const requestBody = {
          ...values,
          highLights: images,
        };
        callApi(
          "POST",
          `${routes.createStage}`,
          requestBody,
          setIsLoading,
          (res) => {
            if (res.status === 200) {
              GreenNotify("Stage Created Successfully");
              toggleModal();
            } else {
              RedNotify(res?.message || "Error Occurred. Please try again later.");
            }
          },
          (error) => {
            RedNotify(
              "An error occurred during the API call. Please try again later.",
              error
            );
          }
        );
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        console.error("Validation Error:", errorInfo);
      });
  };

  return (
    <Modal
      title={"Create Stage"}
      visible={visible}
      onCancel={toggleModal}
      footer={[
        <Button key="cancel" onClick={toggleModal}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Title"
          extra={<TranslationHint />}
          name="title"
          rules={[{ required: true, message: "Please enter the title" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          extra={<TranslationHint />}
          name="description"
          rules={[{ required: true, message: "Please enter the Description" }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>
        <Form.Item
          label="Start Location"
          name="startLocation"
          rules={[
            {required: true, message: "Please enter the Start Location"},
            { validator: mapLinkValidator },
          ]}
          extra={MAP_LINK_HINT}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="End Location"
          name="endLocation"
          rules={[
            {required: true, message: "Please enter the End Location"},
            { validator: mapLinkValidator },
          ]}
          extra={MAP_LINK_HINT}
        >
          <Input />
        </Form.Item>
        {/* The stage's position in the walk - 1 is the first leg, 2 the second. Lists are
            sorted by this, so a route always reads start to finish regardless of the order
            the stages happened to be created in.

            Required, a whole number, and unique within its route: no two stages of the same
            route may claim the same position. Numbering restarts per route, so every route
            runs 1..n independently. The backend enforces all three and returns a message
            naming the clash, which is why the save handler surfaces res.message. */}
        <Form.Item
          label="Order (1 = first leg of the walk)"
          name="order"
          rules={[
            { required: true, message: "Please enter this stage's order" },
            {
              // Whole numbers only. precision={0} on the input stops most decimals being
              // typed, but a pasted "1.5" still reaches validation, so it is rejected here
              // too. The backend checks it again, and also that no other stage on the same
              // route already holds this number.
              validator: (_, value) => {
                if (value === undefined || value === null || value === "") {
                  return Promise.resolve();
                }
                const n = Number(value);
                return Number.isInteger(n) && n >= 1
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Use a whole number of 1 or more - 1, 2, 3, not 1.1")
                    );
              },
            },
          ]}
        >
          <InputNumber
            min={1}
            step={1}
            precision={0}
            style={{ width: "100%" }}
            placeholder="1"
          />
        </Form.Item>
        <Form.Item
          label="Difficulty"
          name="difficulty"
          rules={[{ required: true, message: "Please enter the difficulty" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Route"
          name="route"
          rules={[{ required: true, message: "Please select a Route" }]}
        >
          <Select placeholder="Select a Route">
            {roots.map((root) => (
              <Option key={root._id} value={root._id}>
                {root.title}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div
          onClick={pickImageFile}
          style={{ marginBottom: "2rem" }}
          className="add-product-modal-input-title"
        >
          <h2>HighLights</h2>
          <div className="add-product-modal-image">
            {images.length ? (
              images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Highlight ${index}`}
                  style={{ width: "100px", height: "100px", margin: "0 5px" }}
                />
              ))
            ) : (
              <img src={PLACEHOLDER_IMAGE} alt="Dummy" />
            )}
          </div>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            required
          />
          {fileError && <p style={{ color: "red" }}>{fileError}</p>}
        </div>
      </Form>
    </Modal>
  );
};

export default AddCategory;
